import React, { useState } from "react";
import {
  Card, CircularProgress, Chip,
  TextField, MenuItem, Select, FormControl, InputLabel, Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import axios from "axios";
import { Notify } from "notiflix";
import { ENDPOINT } from "constants";
import { useSelector } from "react-redux";
import usePermission from "hooks/usePermission";

const STATUS_COLOR = {
  pending:  "warning",
  read:     "info",
  replied:  "success",
  archived: "default",
};

const Field = ({ label, children }) => (
  <MDBox mb={3}>
    <MDTypography
      variant="caption" color="secondary" fontWeight="bold"
      textTransform="uppercase" sx={{ letterSpacing: "0.08em" }}
    >
      {label}
    </MDTypography>
    <MDBox mt={0.5}>{children}</MDBox>
  </MDBox>
);

const InquiryDetail = ({ inquiry: initial, onBack, onUpdated }) => {
  const { user } = useSelector((state) => state.auth);
  const token = user?.accessToken;
  const canUpdate = usePermission("leads-management", "update");
  const canDelete = usePermission("leads-management", "delete");

  const [inquiry, setInquiry]         = useState(initial);
  const [status, setStatus]           = useState(initial.status || "pending");
  const [replyMessage, setReplyMessage] = useState("");
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);

  const handleUpdate = async () => {
    try {
      setSaving(true);
      const payload = { status };
      if (status === "replied" && replyMessage.trim()) payload.replyMessage = replyMessage.trim();

      const { data } = await axios.put(
        `${ENDPOINT}api/v1/contact/inquiry/${inquiry._id || inquiry.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInquiry(data.data?.inquiry || inquiry);
      Notify.success("Inquiry updated");
      if (onUpdated) onUpdated();
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to update inquiry");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this inquiry? This cannot be undone.")) return;
    try {
      setDeleting(true);
      const label = inquiry.name || inquiry.subject || inquiry.email || '';
      await axios.delete(
        `${ENDPOINT}api/v1/contact/inquiry/${inquiry._id || inquiry.id}?label=${encodeURIComponent(label)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      Notify.success("Inquiry deleted");
      onBack();
    } catch (err) {
      Notify.failure(err?.response?.data?.message || "Failed to delete inquiry");
    } finally {
      setDeleting(false);
    }
  };

  const createdAt = inquiry.createdAt
    ? new Date(inquiry.createdAt).toLocaleString()
    : null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        {/* Back */}
        <MDBox mb={2}>
          <MDButton variant="text" color="info" size="small" startIcon={<ArrowBackIcon />} onClick={onBack}>
            Back to Inquiries
          </MDButton>
        </MDBox>

        <Card>
          {/* Card header */}
          <MDBox
            mx={2} mt={-3} py={3} px={2}
            variant="gradient" bgColor="info"
            borderRadius="lg" coloredShadow="info"
            display="flex" justifyContent="space-between" alignItems="center"
          >
            <MDTypography variant="h6" color="white">
              Inquiry Detail
            </MDTypography>
            <Chip
              label={inquiry.status || "pending"}
              color={STATUS_COLOR[inquiry.status] || "default"}
              size="small"
              sx={{ color: "white", fontWeight: 700, textTransform: "capitalize" }}
            />
          </MDBox>

          <MDBox p={4}>
            {/* Meta row */}
            <MDBox display="flex" flexWrap="wrap" gap={4} mb={3}>
              {inquiry.email && (
                <Field label="Email">
                  <MDTypography variant="body2" color="text" fontWeight="medium">
                    {inquiry.email}
                  </MDTypography>
                </Field>
              )}
              <Field label="Source">
                <MDTypography variant="body2" color="text" sx={{ fontFamily: "monospace" }}>
                  {inquiry.source || "—"}
                </MDTypography>
              </Field>
              {createdAt && (
                <Field label="Received">
                  <MDTypography variant="body2" color="text">
                    {createdAt}
                  </MDTypography>
                </Field>
              )}
            </MDBox>

            <Divider sx={{ mb: 3 }} />

            {/* Message */}
            <Field label="Message">
              <MDBox
                p={2.5}
                sx={{ background: "#f8f9fa", borderRadius: 2, border: "1px solid #e9ecef" }}
              >
                <MDTypography variant="body2" color="text" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.8 }}>
                  {inquiry.message || "—"}
                </MDTypography>
              </MDBox>
            </Field>

            <Divider sx={{ mb: 3 }} />

            {/* Status update */}
            <MDTypography variant="subtitle2" fontWeight="bold" mb={2}>
              Update Status
            </MDTypography>
            <MDBox display="flex" flexWrap="wrap" gap={2} alignItems="flex-end">
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Status</InputLabel>
                <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="read">Read</MenuItem>
                  <MenuItem value="replied">Replied</MenuItem>
                  <MenuItem value="archived">Archived</MenuItem>
                </Select>
              </FormControl>

              {status === "replied" && (
                <TextField
                  label="Reply message (sends email)"
                  multiline
                  rows={3}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  sx={{ flex: 1, minWidth: 280 }}
                  size="small"
                />
              )}
            </MDBox>

            {/* Actions */}
            <MDBox mt={3} display="flex" gap={2}>
              {canUpdate && (
                <MDButton variant="gradient" color="info" onClick={handleUpdate} disabled={saving}>
                  {saving ? <CircularProgress size={18} color="inherit" /> : "Save Changes"}
                </MDButton>
              )}
              {canDelete && (
                <MDButton variant="outlined" color="error" onClick={handleDelete} disabled={deleting}>
                  {deleting ? <CircularProgress size={18} color="inherit" /> : "Delete Inquiry"}
                </MDButton>
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default InquiryDetail;
